<template>
	<div class="relationship-types-selector dx-card" v-if="!simpleMode">
		<div style="text-decoration: underline; font-weight: 500; margin-bottom: 5px; text-align: center;">Blueprints allowed in this relationship</div>
		<DxTagBox :multiple="true" :search-enabled="true" :data-source="relationSchemaSelector" display-expr="plural" value-expr="keyspec" :value="internalItems.aType" @value-changed="handleChanges(true, $event)" />
		<div class="relation-arrow">
			<i class="dx-icon-sorted"></i>
		</div>
		<DxTagBox :multiple="true" :search-enabled="true" :data-source="relationSchemaSelector" display-expr="plural" value-expr="keyspec" :value="internalItems.bType" @value-changed="handleChanges(false, $event)" />
		<div class="dx-info-message reduce-padding" style="margin: 10px 0;">
			<i class="dx-icon-info"></i>
			<div class="dx-info-message-text">You can link to any number of blueprint types - relations are always two-way. But you can't use the same blueprint on both sides.</div>
		</div>
	</div>
	<div class="relationship-types-selector dx-card" v-else>
		<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
			<div style="font-weight: 500;">This relationship leads from <u>{{ internalItems.aType[0] }}</u> to...</div>
			<div>
				<DxButton icon="insertrowabove" text="Advanced" @click="simpleMode = false" />
			</div>
		</div>
		<DxSelectBox :search-enabled="true" :data-source="relationSchemaSelector" display-expr="plural" value-expr="keyspec" :value="internalItems.bType[0]" @value-changed="handleChanges(false, $event)" ref="simpleTypeSelector" />
	</div>
	<div class="relationship-types-selector dx-card" v-if="internalItems.aType.length > 0 && internalItems.bType.length > 0" style="display: flex;">
		<div style="flex: 1 1 50%; padding: 0 10px 0 0; border-right: 2px dotted var(--dx-color-border); text-align: center;">
			<div class="relationship-detail-header">{{ internalItems.aType.join(', ') }}</div>
			<div>Each {{ internalItemsSchemas.aType.map(a => a.singular).join('/') }} can have</div>
			<div style="margin-bottom: 15px;">
				<DxSelectBox :items="degreeOptions.a" value-expr="value" display-expr="text" :value="internalItems.aDegree" @value-changed="internalItems.aDegree = $event.value; emitChanged()" stylingMode="outlined" class="centered-select" />
			</div>
			<div>The unique identifier for this is:</div>
			<div><DxTextBox :value="internalItems.aKey" @value-changed="internalItems.aKey = $event.value; emitChanged()" /></div>
			<div v-if="collisionWarn.a.length > 0" class="collision-warning">Already exists on <i>{{ collisionWarn.a.join(', ') }}</i>.</div>
		</div>
		<div style="flex: 1 1 50%; padding: 0 0 0 10px; text-align: center;">
			<div class="relationship-detail-header">{{ internalItems.bType.join(', ') }}</div>
			<div>Each {{ internalItemsSchemas.bType.map(b => b.singular).join('/') }} can have</div>
			<div style="margin-bottom: 15px;">
				<DxSelectBox :items="degreeOptions.b" value-expr="value" display-expr="text" :value="internalItems.bDegree" @value-changed="internalItems.bDegree = $event.value; emitChanged()" stylingMode="outlined" class="centered-select" />
			</div>
			<div>The unique identifier for this is:</div>
			<div><DxTextBox :value="internalItems.bKey" @value-changed="internalItems.bKey = $event.value; emitChanged()" /></div>
			<div v-if="collisionWarn.b.length > 0" class="collision-warning">Already exists on <i>{{ collisionWarn.b.join(', ') }}</i>.</div>
		</div>
	</div>
	<div class="dx-info-message reduce-padding" style="margin: 10px 0;" v-if="internalItems.aType.length > 0 && internalItems.bType.length > 0">
		<i class="dx-icon-info"></i>
		<div class="dx-info-message-text">As an example, for a customer and invoice: <i>customer.invoices</i> describes the many invoices for a customer, <i>invoice.from_customer</i> describes the one customer for a given invoice.</div>
	</div>
</template>

<script setup>
/* eslint-disable */
// Component for selection the aType and bType schema types for a relationship, as well as associated meta values: unique identifier for each side, degree/valence.
// Each relationship can be restricted to one or more basic schema types, e.g. customer -> invoice, or person/organize -> donation.
// We consider each side the same - relations are always two-way.

import { ref, defineProps, defineEmits, watch, computed } from 'vue';
import { DxTagBox } from 'devextreme-vue/tag-box';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxButton } from 'devextreme-vue/button';
import { DxTextBox } from 'devextreme-vue/text-box';
import DataSource from 'devextreme/data/data_source';
import { alert as dxAlert } from 'devextreme/ui/dialog';
import { SchemaCache } from '../schemas.js';

const props = defineProps({
	initial: {
		type: Object,
		default: () => ({ aType: [], bType: [] })
	},
});
const emit = defineEmits(['changed']);
const simpleMode = ref(true);
const simpleTypeSelector = ref(null);

const internalItemsSchemas = computed(() => {
	let ret = JSON.parse(JSON.stringify(internalItems.value));
	ret.aType = ret.aType.map(a => SchemaCache.getSchemaByType(a));
	ret.bType = ret.bType.map(b => SchemaCache.getSchemaByType(b));
	return ret;
});

// Generate arrays of schema types that have a collision with the current aKey/bKey values.
const collisionWarn = computed(() => {
	// TODO: Deal with existing editing where this will already exist for ourselves!
	let collisions = { a: [], b: [] };
	if (!internalItems.value.aKey || !internalItems.value.bKey || internalItemsSchemas.value.aType.length <= 0 || internalItemsSchemas.value.bType.length <= 0)
		return collisions;
	for (let a of internalItemsSchemas.value.aType) {
		if (a.fields.some(f => f.field == internalItems.value.aKey))
			collisions.a.push(a.type);
	}
	for (let b of internalItemsSchemas.value.bType) {
		if (b.fields.some(f => f.field == internalItems.value.bKey))
			collisions.b.push(b.type);
	}
	return {
		a: collisions.a,
		b: collisions.b,
	};
});

const degreeOptions = computed(() => {
	if (internalItems.value.aType.length <= 0 || internalItems.value.bType.length <= 0)
		return { a: [], b: [] };
	return {
		a: [ { text: 'Many ' + internalItemsSchemas.value.bType.map(b => b.plural).join('/'), value: 'many' }, { text: 'One ' + internalItemsSchemas.value.bType.map(b => b.singular).join('/'), value: 'one' } ],
		b: [ { text: 'Many ' + internalItemsSchemas.value.aType.map(a => a.plural).join('/'), value: 'many' }, { text: 'One ' + internalItemsSchemas.value.aType.map(a => a.singular).join('/'), value: 'one' } ],
	};
})

const internalItems = ref({
	aType: [], bType: [],
	aKey: '', bKey: '',
	aDegree: '', bDegree: '',
});
watch(props, () => {
	// Always take a copy, just in case something is up. They should just be arrays of strings.
	internalItems.value = {
		aType: props.initial && Array.isArray(props.initial.aType) ? JSON.parse(JSON.stringify(props.initial.aType)) : [],
		bType: props.initial && Array.isArray(props.initial.bType) ? JSON.parse(JSON.stringify(props.initial.bType)) : [],
		aKey: props.initial && props.initial.aKey ? props.initial.aKey : '',
		bKey: props.initial && props.initial.bKey ? props.initial.bKey : '',
		aDegree: props.initial && props.initial.aDegree ? props.initial.aDegree : '',
		bDegree: props.initial && props.initial.bDegree ? props.initial.bDegree : '',
	};
	// Simple mode can only be used if we have only 1 item on each side.
	simpleMode.value = internalItems.value.aType.length <= 1 && internalItems.value.bType.length <= 1;
}, { immediate: true });

// Data source for chosing the aType/bType values for a relationship.
const relationSchemaSelector = new DataSource({
	loadMode: 'raw',
	key: 'keyspec',
	load: async () => {
		await SchemaCache.waitForData();
		return SchemaCache.getAllSchemasExceptRelations();
	},
});

// Handle the main schema changes selection in simple/advanced mode.
const handleChanges = (isA, e) => {
	// In simple mode, if we try to set a the bType to the same value as our aType, we can cancel this change with a warning.
	if (simpleMode.value && [e.value].some(v => internalItems.value.aType.includes(v))) {
		dxAlert('You cannot link to the same blueprint on both sides of a relationship.', 'Relationship Error');
		simpleTypeSelector.value.instance.clear();
		return;
	}
	internalItems.value[isA ? 'aType' : 'bType'] = simpleMode.value ? (e.value ? [e.value] : []) : e.value;
	// Auto-generate useful aKey/bKey default values, if we have a valid display for them.
	if (internalItems.value.aType.length > 0 && internalItems.value.bType.length > 0) {
		if (internalItems.value.aKey == '')
			internalItems.value.aKey =  String(internalItems.value.bType[0]) + (internalItems.value.bType[0].endsWith('s') ? '' : 's');
		if (internalItems.value.bKey == '')
			internalItems.value.bKey = String(internalItems.value.aType[0]) + (internalItems.value.aType[0].endsWith('s') ? '' : 's');
	}
	emitChanged();
};
const emitChanged = () => {
	emit('changed', {
		aType: JSON.parse(JSON.stringify(internalItems.value.aType)),
		bType: JSON.parse(JSON.stringify(internalItems.value.bType)),
		aKey: internalItems.value.aKey,
		bKey: internalItems.value.bKey,
		aDegree: internalItems.value.aDegree,
		bDegree: internalItems.value.bDegree,
	});
}

</script>

<style lang="scss">
.relationship-types-selector {
	margin: 0 0 20px 0;
	padding: 10px;

	.relation-arrow {
		text-align: center;
		padding: 0;
		
		i {
			font-size: 3em;
		}
	}
	.relationship-detail-header {
		border-bottom: 1px solid var(--dx-color-text);
		margin-bottom:10px;
		font-weight: 500;
	}
	.centered-select.dx-selectbox input {  
		text-align: center;  
	}
	.collision-warning {
		color: var(--dx-color-danger);
		font-size: 0.9em;
		margin-top: 5px;
	}
}
</style>
